<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';

const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);
const currentUser = computed(() => store.getters.getCurrentUser);
const canvasRef = ref(null);
const chatCount = ref(0);
const qualCount = ref(0);
const followCount = ref(0);
let graph = null;

function getAuthHeaders() {
  const token = currentUser.value?.access_token || '';
  return { api_access_token: token };
}

async function apiGet(path) {
  try {
    const resp = await fetch(`/api/v1/accounts/${accountId.value}${path}`, { headers: getAuthHeaders() });
    return resp.ok ? await resp.json() : null;
  } catch { return null; }
}

class ForceGraph {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.nodes = []; this.edges = [];
    this.W = 0; this.H = 0; this.running = false; this.dragNode = null;
    this.SPRING = 0.015; this.REPULSION = 400; this.GRAVITY = 0.008; this.DAMPING = 0.85;
    this._mx = undefined; this._my = undefined;

    canvas.addEventListener('mousemove', (e) => {
      const rect = canvas.getBoundingClientRect();
      this._mx = e.clientX - rect.left; this._my = e.clientY - rect.top;
      if (this.dragNode) { this.dragNode.x = this._mx; this.dragNode.y = this._my; this.dragNode.vx = 0; this.dragNode.vy = 0; }
    });
    canvas.addEventListener('mouseleave', () => { this._mx = undefined; this._my = undefined; this.dragNode = null; });
    canvas.addEventListener('mousedown', (e) => {
      const rect = canvas.getBoundingClientRect(); const mx = e.clientX - rect.left; const my = e.clientY - rect.top;
      for (let i = this.nodes.length - 1; i >= 0; i--) {
        const n = this.nodes[i]; if ((mx - n.x) ** 2 + (my - n.y) ** 2 < (n.r + 5) ** 2) { this.dragNode = n; break; }
      }
    });
    canvas.addEventListener('mouseup', () => { this.dragNode = null; });
  }

  resize() {
    const rect = this.canvas.parentElement.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = rect.width * dpr; this.canvas.height = rect.height * dpr;
    this.canvas.style.width = rect.width + 'px'; this.canvas.style.height = rect.height + 'px';
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.W = rect.width; this.H = rect.height;
  }

  addNode(id, label, x, y, r, type) {
    const n = { id, label: label || '', x: x || this.W / 2 + Math.random() * 100 - 50, y: y || this.H / 2 + Math.random() * 100 - 50, vx: 0, vy: 0, r: r || 4, type: type || 'small' };
    this.nodes.push(n); return n;
  }

  addEdge(a, b) { this.edges.push({ a, b }); }

  tick() {
    const ns = this.nodes;
    for (let i = 0; i < ns.length; i++) for (let j = i + 1; j < ns.length; j++) {
      const dx = ns[j].x - ns[i].x, dy = ns[j].y - ns[i].y, d = Math.sqrt(dx * dx + dy * dy) || 1;
      const isCenter = ns[i].type === 'center' || ns[j].type === 'center';
      const isInbox = ns[i].type === 'inbox' || ns[j].type === 'inbox';
      const rep = isCenter ? this.REPULSION * 5 : isInbox ? this.REPULSION * 2 : this.REPULSION;
      const f = rep / (d * d); const fx = dx / d * f, fy = dy / d * f;
      ns[i].vx -= fx; ns[i].vy -= fy; ns[j].vx += fx; ns[j].vy += fy;
    }
    for (const e of this.edges) {
      const dx = e.b.x - e.a.x, dy = e.b.y - e.a.y, d = Math.sqrt(dx * dx + dy * dy) || 1;
      const rest = e.a.type === 'center' || e.b.type === 'center' ? 180 : e.a.type === 'inbox' || e.b.type === 'inbox' ? 80 : 50;
      const f = (d - rest) * this.SPRING; const fx = dx / d * f, fy = dy / d * f;
      e.a.vx += fx; e.a.vy += fy; e.b.vx -= fx; e.b.vy -= fy;
    }
    const cx = this.W / 2, cy = this.H / 2;
    for (const n of ns) {
      if (n === this.dragNode) continue;
      const grav = n.type === 'center' ? this.GRAVITY * 8 : this.GRAVITY;
      n.vx += (cx - n.x) * grav; n.vy += (cy - n.y) * grav;
      n.vx *= this.DAMPING; n.vy *= this.DAMPING;
      n.x += n.vx; n.y += n.vy;
      n.x = Math.max(40, Math.min(this.W - 40, n.x));
      n.y = Math.max(40, Math.min(this.H - 40, n.y));
    }
  }

  draw() {
    const ctx = this.ctx;
    const isDark = document.body.classList.contains('dark');
    ctx.fillStyle = isDark ? '#1F2125' : '#F8F8FA';
    ctx.fillRect(0, 0, this.W, this.H);

    ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    ctx.lineWidth = 1;
    for (const e of this.edges) { ctx.beginPath(); ctx.moveTo(e.a.x, e.a.y); ctx.lineTo(e.b.x, e.b.y); ctx.stroke(); }

    let hovered = null;
    if (this._mx !== undefined) {
      for (let i = this.nodes.length - 1; i >= 0; i--) {
        const n = this.nodes[i]; if ((this._mx - n.x) ** 2 + (this._my - n.y) ** 2 < (n.r + 6) ** 2) { hovered = n; break; }
      }
    }
    this.canvas.style.cursor = hovered ? 'pointer' : 'default';

    for (const n of this.nodes) {
      if (n.type === 'center') {
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = '#388E3C'; ctx.fill();
        ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillStyle = '#fff'; ctx.fillText(n.label, n.x, n.y + 4);
      } else if (n.type === 'inbox') {
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = isDark ? '#282A2E' : '#fff'; ctx.fill();
        ctx.strokeStyle = '#388E3C'; ctx.lineWidth = 2; ctx.stroke();
        ctx.font = '500 10px Inter,sans-serif'; ctx.textAlign = 'center';
        const tw = ctx.measureText(n.label).width;
        ctx.fillStyle = isDark ? 'rgba(40,42,46,0.9)' : 'rgba(235,235,235,0.85)';
        ctx.fillRect(n.x - tw / 2 - 4, n.y + n.r + 6, tw + 8, 16);
        ctx.fillStyle = isDark ? '#E5E7EB' : '#525252';
        ctx.fillText(n.label, n.x, n.y + n.r + 18);
      } else {
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        const c = n.type === 'followup_conv' ? (n.r >= 4 ? '#FF5500' : 'rgba(255,85,0,0.35)') : (n.r >= 4 ? '#388E3C' : 'rgba(56,142,60,0.35)');
        ctx.fillStyle = c; ctx.fill();
      }
    }

    if (hovered && (hovered.type === 'conv' || hovered.type === 'followup_conv') && hovered.label) {
      ctx.font = '500 11px Inter,sans-serif';
      const tw = ctx.measureText(hovered.label).width;
      const tx = hovered.x, ty = hovered.y - hovered.r - 14;
      ctx.fillStyle = 'rgba(38,38,38,0.9)';
      ctx.beginPath();
      ctx.roundRect(tx - tw / 2 - 6, ty - 10, tw + 12, 20, 4);
      ctx.fill();
      ctx.fillStyle = '#fff'; ctx.textAlign = 'center'; ctx.fillText(hovered.label, tx, ty + 3);
    }
  }

  loop() { if (!this.running) return; this.tick(); this.draw(); requestAnimationFrame(() => this.loop()); }
  start() { this.resize(); for (let i = 0; i < 200; i++) this.tick(); this.running = true; this.loop(); }
  stop() { this.running = false; }
}

async function initGraph() {
  const canvas = canvasRef.value;
  if (!canvas || graph) return;

  graph = new ForceGraph(canvas);
  const W = 500, H = 350;
  const iris = graph.addNode('iris', 'IRIS', W * 0.5, H * 0.45, 24, 'center');

  try {
    const [inboxData, qualData, followData] = await Promise.all([
      apiGet('/inboxes'),
      apiGet('/conversations?labels[]=qualificacao-em-andamento&page=1'),
      apiGet('/conversations?labels[]=followup-ativo&page=1'),
    ]);

    const inboxNames = {};
    (inboxData?.payload || []).forEach(ib => { inboxNames[ib.id] = ib.name; });

    const qualConvs = qualData?.data?.payload || [];
    const followConvs = followData?.data?.payload || [];
    qualCount.value = qualData?.data?.meta?.all_count || qualConvs.length;
    followCount.value = followData?.data?.meta?.all_count || followConvs.length;
    chatCount.value = qualCount.value + followCount.value;

    const byInbox = {};
    qualConvs.forEach(c => { const id = c.inbox_id || 0; if (!byInbox[id]) byInbox[id] = []; byInbox[id].push(c); });
    const followByInbox = {};
    followConvs.forEach(c => { const id = c.inbox_id || 0; if (!followByInbox[id]) followByInbox[id] = []; followByInbox[id].push(c); });

    const allInboxIds = [...new Set([...Object.keys(byInbox), ...Object.keys(followByInbox)])];
    const angleStep = (Math.PI * 2) / Math.max(allInboxIds.length, 1);

    allInboxIds.forEach((key, idx) => {
      const angle = angleStep * idx - Math.PI / 2;
      const ix = W / 2 + Math.cos(angle) * 120, iy = H / 2 + Math.sin(angle) * 120;
      const label = inboxNames[parseInt(key)] || ('Inbox ' + key);
      const inboxNode = graph.addNode('inbox_' + key, label, ix, iy, 14, 'inbox');
      graph.addEdge(iris, inboxNode);

      (byInbox[key] || []).forEach((conv, ci) => {
        const ca = (Math.PI * 2 / Math.max((byInbox[key] || []).length, 1)) * ci;
        const cd = 35 + Math.random() * 20;
        const name = conv.meta?.sender?.name || ('Chat #' + conv.id);
        const cn = graph.addNode('conv_' + conv.id, name, ix + Math.cos(ca) * cd, iy + Math.sin(ca) * cd, 2 + Math.random() * 3, 'conv');
        graph.addEdge(inboxNode, cn);
      });

      (followByInbox[key] || []).forEach((fu) => {
        const name = (fu.meta?.sender?.name || ('Follow-up #' + fu.id)) + ' (follow-up)';
        const fn = graph.addNode('fu_' + fu.id, name, ix + Math.random() * 40 - 20, iy + Math.random() * 40 - 20, 2 + Math.random() * 2, 'followup_conv');
        graph.addEdge(inboxNode, fn);
      });
    });

    graph.start();
  } catch (e) {
    console.warn('[SzForceGraph] API error, using mock:', e);
    ['Campanhas', 'Teste', 'Bot AI', 'Suporte'].forEach((name, idx) => {
      const angle = (Math.PI * 2 / 4) * idx - Math.PI / 2;
      const n = graph.addNode('inbox_' + idx, name, W / 2 + Math.cos(angle) * 120, H / 2 + Math.sin(angle) * 120, 14, 'inbox');
      graph.addEdge(iris, n);
      for (let j = 0; j < 3 + Math.floor(Math.random() * 4); j++) {
        const s = graph.addNode('s_' + idx + '_' + j, '', n.x + Math.random() * 50 - 25, n.y + Math.random() * 50 - 25, 2 + Math.random() * 3, 'conv');
        graph.addEdge(n, s);
      }
    });
    graph.start();
  }
}

onMounted(() => { setTimeout(initGraph, 300); });
onUnmounted(() => { if (graph) graph.stop(); });
</script>

<template>
  <div class="sz-graph-wrap">
    <div class="sz-graph-header">
      <div>
        <div class="sz-graph-title">Chart</div>
        <div class="sz-graph-sub">
          <span class="sz-badge-live"><span class="sz-badge-dot" />&nbsp;Atualizado há 1min</span>
        </div>
      </div>
      <div class="sz-graph-badge">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
        {{ chatCount }} chats
      </div>
    </div>
    <div class="sz-graph-canvas-wrap">
      <canvas ref="canvasRef" />
    </div>
  </div>
</template>

<style scoped>
.sz-graph-wrap { display: flex; flex-direction: column; width: 100%; height: 100%; }
.sz-graph-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
.sz-graph-title { font-size: 16px; font-weight: 600; color: #262626; }
.sz-graph-sub { margin-top: 2px; }
.sz-badge-live { display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px; border-radius: 9999px; background: rgba(44,123,44,0.12); font-size: 11px; font-weight: 500; color: #2C7B2C; }
.sz-badge-dot { width: 6px; height: 6px; border-radius: 50%; background: #2C7B2C; }
.sz-graph-badge { display: flex; align-items: center; gap: 4px; padding: 6px 12px; border: 1px solid #E5E5E5; border-radius: 8px; font-size: 13px; font-weight: 500; color: #262626; background: #fff; }
.sz-graph-canvas-wrap { flex: 1; min-height: 0; border-radius: 8px; overflow: hidden; }
.sz-graph-canvas-wrap canvas { display: block; width: 100%; height: 100%; }
</style>

<style>
/* Dark mode */
.dark .sz-graph-title { color: #EDEEEF; }
.dark .sz-graph-badge { background: #1F2125; border-color: #363840; color: #EDEEEF; }
.dark .sz-badge-live { background: rgba(56,142,60,0.15); color: #4ADE80; }
.dark .sz-badge-dot { background: #4ADE80; }
.dark .sz-graph-wrap { color: #E5E7EB; }
.dark .sz-graph-stats span { color: #9CA3AF; }
</style>
